/**
 * What the leaderboard SAYS, as data — Blockari's `screens/Leaderboard.tsx` constants (c258375). The DOM half
 * (`leaderboard.ts`) only draws these, so the boards, their words and who is paid a chest are tested in node.
 */

import { CHEST_TABLE, type Rarity } from '@/game/chests';
import type { LeaderboardBoard, LeaderboardPeriod } from '@/net/api';

export interface BoardDef {
  readonly value: LeaderboardBoard;
  /** The pill's text. */
  readonly label: string;
  /** The board's full name: the panel's and (where it differs from the pill) the pill's accessible name. */
  readonly name: string;
  /** Pays a weekly chest on its top three (the server's PAID_BOARDS). */
  readonly paid: boolean;
}

/**
 * The five metric pills, in Blockari's order. Blockari's "Combos" is the CARD STREAK here (the in-game streak multiplier:
 * "Win streaks" is a board too, so the in-game one is named for cards). The $CHAIN pill reads "$CHAIN" to leave room for
 * five on one line; its accessible name is the full "Net $CHAIN" (the number is a net and may be negative).
 */
export const BOARDS: readonly BoardDef[] = [
  { value: 'xp', label: 'XP', name: 'XP', paid: true },
  { value: 'won', label: '$CHAIN', name: 'Net $CHAIN', paid: true },
  { value: 'score', label: 'Best score', name: 'Best score', paid: true },
  { value: 'combo', label: 'Card streaks', name: 'Card streaks', paid: true },
  { value: 'streak', label: 'Win streaks', name: 'Win streaks', paid: true },
];

/** The pills as the segmented control takes them: an accessible name only where it differs from the text. */
export const BOARD_OPTIONS = BOARDS.map((b) => ({
  value: b.value,
  label: b.label,
  ...(b.label !== b.name ? { ariaLabel: b.name } : {}),
}));

export const PERIODS: readonly { value: LeaderboardPeriod; label: string }[] = [
  { value: 'week', label: 'This week' },
  { value: 'all', label: 'All time' },
];

export const isBoard = (v: string | null | undefined): v is LeaderboardBoard => BOARDS.some((b) => b.value === v);

export const boardName = (board: LeaderboardBoard): string => BOARDS.find((b) => b.value === board)?.name ?? '';

/** The weekly podium's chest, by place (CHEST_TABLE.weeklyTopStart: legendary, epic, rare). */
export function chestFor(position: number): Rarity | null {
  return position >= 1 && position <= 3 ? (CHEST_TABLE.weeklyTopStart[position - 1] ?? null) : null;
}

/** The chest a weekly place is on track for: a positive number only (a loss pays nothing), on a paid board only. */
export function weeklyChest(board: LeaderboardBoard, period: LeaderboardPeriod, position: number, value: number): Rarity | null {
  const paid = BOARDS.find((b) => b.value === board)?.paid ?? false;
  return period === 'week' && paid && value > 0 ? chestFor(position) : null;
}

/** The empty board's line. */
export function emptyBoard(board: LeaderboardBoard, period: LeaderboardPeriod): string {
  const what = board === 'combo' ? 'card streaks' : board === 'streak' ? 'win streaks' : null;
  if (what) return period === 'week' ? `No ${what} yet this week` : `No ${what} yet`;
  return period === 'week' ? 'No games yet this week' : 'No games yet';
}

/**
 * A board's number as text and its unit: XP and scores as thousands, the net signed, a card streak as its multiplier
 * ("5X": the server sends the multiplier itself), a win streak as wins. `unit` is the small caption beside it.
 */
export function boardText(board: LeaderboardBoard, value: number): { text: string; unit: string | null } {
  const signed = (n: number): string => (n === 0 ? '0' : `${n < 0 ? '−' : '+'}${Math.abs(n).toLocaleString()}`);
  if (board === 'won') return { text: signed(value), unit: '$CHAIN' };
  if (board === 'combo') return { text: `${value}X`, unit: null };
  if (board === 'streak') return { text: value.toLocaleString(), unit: value === 1 ? 'win' : 'wins' };
  return { text: value.toLocaleString(), unit: board === 'xp' ? 'XP' : null };
}

